import { ArrowLeft, Check, ChevronDown, Search, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { carriersApiService } from '@/services/api/carriersApiService';

type CarrierOption = {
  id: string;
  name: string;
  normalizedName: string;
  aliases?: string[];
};

export interface CarrierNameSelectProps {
  value?: string | null;
  onChange: (name: string, carrierId?: string | null) => void;
  onSelectDirectory?: (carrierId: string, divisionId?: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export const CarrierNameSelect: React.FC<CarrierNameSelectProps> = ({
  value,
  onChange,
  onSelectDirectory,
  disabled,
  placeholder = 'Select carrier',
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [stage, setStage] = useState<'carrier' | 'division'>('carrier');
  const [activeCarrier, setActiveCarrier] = useState<CarrierOption | null>(
    null,
  );

  // Fetch all carriers (client-side filter)
  const { data: carriers = [] } =
    carriersApiService.useListCarriersQuery(undefined);
  const { data: divisions = [] } = carriersApiService.useListDivisionsQuery(
    { carrierId: activeCarrier?.id || '' },
    { skip: !activeCarrier?.id || stage !== 'division' },
  );

  const options: CarrierOption[] = useMemo(
    () =>
      carriers.map((c) => ({
        id: c.id,
        name: c.name,
        normalizedName: c.normalizedName,
        aliases: c.aliases,
      })),
    [carriers],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        (o.aliases || []).some((a) => a.toLowerCase().includes(q)),
    );
  }, [options, query]);

  const selected = useMemo(() => {
    if (!value) return undefined;
    return options.find(
      (o) => o.name.toLowerCase() === (value || '').toLowerCase(),
    );
  }, [value, options]);

  return (
    <div className="flex items-center gap-2">
      <Popover
        open={open}
        onOpenChange={(v) => {
          if (!v) {
            setStage('carrier');
            setActiveCarrier(null);
            setQuery('');
          }
          setOpen(v);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={className}
            disabled={disabled}
          >
            <span className="truncate min-w-[12rem]">
              {selected?.name || value || placeholder}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="start">
          {stage === 'carrier' ? (
            <>
              <div className="p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Type to search or enter a custom name…"
                    className="pl-8 pr-2 h-8"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && query.trim()) {
                        onChange(query.trim(), null);
                        setOpen(false);
                      }
                    }}
                  />
                </div>
                {query.trim() && (
                  <div className="mt-2">
                    <button
                      type="button"
                      className="text-xs text-blue-700 hover:underline"
                      onClick={() => {
                        onChange(query.trim(), null);
                        setOpen(false);
                      }}
                    >
                      Use “{query.trim()}”
                    </button>
                  </div>
                )}
              </div>
              <div className="max-h-72 overflow-auto py-1">
                {filtered.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No matching carriers
                  </div>
                ) : (
                  filtered.map((opt) => {
                    const isSelected =
                      selected && opt.id === selected.id && value === opt.name;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                        onClick={() => {
                          // Update the visible value immediately on carrier pick
                          onChange(opt.name, opt.id);
                          setActiveCarrier(opt);
                          setStage('division');
                        }}
                      >
                        <span className="truncate">{opt.name}</span>
                        {isSelected ? (
                          <Check className="h-4 w-4 text-blue-600" />
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              <div className="p-2 border-b flex items-center gap-2">
                <button
                  type="button"
                  className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-gray-50"
                  onClick={() => setStage('carrier')}
                  aria-label="Back to carriers"
                >
                  <ArrowLeft className="h-4 w-4 text-gray-600" />
                </button>
                <div className="text-sm font-medium text-gray-900 truncate">
                  {activeCarrier?.name}
                </div>
              </div>
              <div className="max-h-72 overflow-auto py-1">
                {divisions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No divisions found
                  </div>
                ) : (
                  divisions.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                      onClick={() => {
                        if (activeCarrier) {
                          onChange(activeCarrier.name, activeCarrier.id);
                          if (typeof onSelectDirectory === 'function') {
                            onSelectDirectory(activeCarrier.id, d.id);
                          }
                        }
                        setOpen(false);
                      }}
                    >
                      <span className="truncate">{d.divisionName}</span>
                      {d.isDefault ? (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                          default
                        </span>
                      ) : null}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>
      {value ? (
        <button
          type="button"
          aria-label="Clear carrier"
          className="h-8 w-8 inline-flex items-center justify-center rounded border border-gray-200 hover:bg-gray-50 text-gray-500"
          onClick={() => onChange('', null)}
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
};
